import { Facebook, Linkedin, Instagram, Youtube, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import octopusLogo from "@/assets/octopus-logo-official.png";
import humanScoreLogo from "@/assets/human-score-logo.png.asset.json";

const Footer = () => {
  return (
    <footer className="py-16 bg-card border-t border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={octopusLogo} alt="TOM logo" className="h-8 w-8" loading="lazy" />
              <span className="font-heading font-bold text-foreground">the Octopus Movement</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A global non-profit consortium of nonlinear thinkers creating possibilities for a better world.
            </p>

            {/* Quality achievement badge */}
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 font-semibold mb-3">
                Quality Achievement
              </p>
              <Link
                to="/human-score"
                aria-label="Our A-level Human Score, learn more"
                className="inline-block transform-gpu transition-transform duration-200 hover:scale-[1.03]"
              >
                <img
                  src={humanScoreLogo.url}
                  alt="Human Score for Communities, A-level rating achieved by the Octopus Movement"
                  className="h-16 w-auto"
                  loading="lazy"
                />
              </Link>
            </div>

            {/* Community Platform */}
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 font-semibold mb-3">
                Human Mycelium
              </p>
              <a
                href="https://theoctopusmovement.disco.co/join"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200"
                aria-label="Join the Human Mycelium platform on Disco"
              >
                Join the Human Mycelium Platform
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" x2="21" y1="14" y2="3" />
                </svg>
              </a>
              <p className="text-xs text-muted-foreground/70 mt-2 leading-relaxed">
                Connect with fellow nonlinear thinkers, join conversations, and explore member spaces.
              </p>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Documents</h4>
            <div className="flex flex-col gap-2">
              <a
                href="/sustainability"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Sustainability
              </a>
              <a
                href="/human-rights-policy"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Human Rights Policy
              </a>
              <a
                href="/privacy-statement"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Privacy Statement
              </a>
              <a
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Terms of Use
              </a>
            </div>

            <h4 className="font-heading font-semibold text-foreground mb-4 mt-8">For Businesses</h4>
            <div className="flex flex-col gap-2">
              <a
                href="https://octopus-thinktank.institute/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Think Tank Session
              </a>
              <a
                href="https://humanscore.info/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Human Score Dashboard
              </a>
            </div>

            <h4 className="font-heading font-semibold text-foreground mb-4 mt-8">Books</h4>
            <div className="flex flex-col gap-2">
              <a
                href="https://octopusleadership.store/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Octopus Leadership
              </a>
            </div>

            <h4 className="font-heading font-semibold text-foreground mb-4 mt-8">Partnerships</h4>
            <div className="flex flex-col gap-2">
              <Link
                to="/become-a-partner"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Become a Partner
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Explanations</h4>
            <div className="flex flex-col gap-2">
              <Link
                to="/tom-explained"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                tOM Explained
              </Link>
              <Link
                to="/why-an-octopus"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Why an Octopus
              </Link>
              <Link
                to="/surprise"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Surprise
              </Link>
              <Link
                to="/nonlinear-thinking"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Nonlinear Thinking
              </Link>
              <Link
                to="/human-mycelium"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Human Mycelium
              </Link>
              <Link
                to="/think-tank"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Think Tank
              </Link>
              <a
                href="/telltom"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                TellTOM App
              </a>
              <Link
                to="/human-score"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Human Score
              </Link>
              <Link
                to="/mycelial-connection"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Mycelial Connection
              </Link>
              <Link
                to="/kids-council"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Kids Council
              </Link>
            </div>

            <h4 className="font-heading font-semibold text-foreground mb-4 mt-8">Project Connection</h4>
            <div className="flex flex-col gap-2">
              <a
                href="https://projecteindhoven.theoctopusmovement.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Project Eindhoven
              </a>
              <span className="text-sm text-muted-foreground/70 cursor-default">
                Project New York
              </span>
              <Link
                to="/project-octopus"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Project Octopus
              </Link>
            </div>
          </div>

          {/* Engine Room */}
          <div>
            <a
              href="https://members.theoctopusmovement.org/home"
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading font-semibold text-foreground mb-4 inline-block hover:text-primary hover:translate-x-1 transition-all duration-200"
            >
              Engine Room
            </a>
            <div className="flex flex-col gap-2">
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                dr. Octopus Meets
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Nonlinear Library
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                iBUILD
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Public Appearances
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Human Score Collection
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Brain Lunches
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                AI
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Think Tanks & TellTOM
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Meet a FM'er
              </a>
              <a
                href="https://members.theoctopusmovement.org/home"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                dr. Octopus Log
              </a>
            </div>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Support</h4>
            <div className="flex flex-col gap-2">
              <a
                href="https://patreon.com/TheOctopusMovement"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Donate on Patreon
              </a>
              <a
                href="/membership"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Membership Overview
              </a>
              <a
                href="/octopus-anonymous"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Octopus Anonymous
              </a>
              <a
                href="https://airtable.com/appj9naWTSEyBxyub/shrahOKBrQHWbDb65"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Join the Movement
              </a>
              <a
                href="/founding-members"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Become a Founding Member
              </a>
              <a
                href="/science-member"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Become a Science Member
              </a>
              <a
                href="/wisdom-membership"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Become a Wisdom Member
              </a>
              <a
                href="/octopus-anonymous"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Octopus Anonymous
              </a>
              <a
                href="/donate"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Directly Donate
              </a>
              <Link
                to="/shop"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Shop Stickers
              </Link>
              <a
                href="/contact"
                className="text-sm text-muted-foreground hover:text-foreground hover:translate-x-1 transition-all duration-200"
              >
                Contact
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} the Octopus Movement. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            {[
              { href: "https://www.facebook.com/groups/theoctopusmovement", icon: Facebook, label: "Facebook" },
              { href: "https://www.linkedin.com/company/the-octopus-movement", icon: Linkedin, label: "LinkedIn" },
              { href: "https://www.instagram.com/theoctopusmovement/", icon: Instagram, label: "Instagram" },
              { href: "https://www.youtube.com/channel/UC0Z0zbmjUTnyBv_YIcMqgMw", icon: Youtube, label: "YouTube" },
              { href: "https://chat.whatsapp.com/LwB7iufzdkI9x253JSSUF8?mode=gi_t", icon: MessageCircle, label: "WhatsApp" },
            ].map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-all duration-200"
                aria-label={label}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
